var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
var _a;
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import '../../styles/widgets/cards/card.scss';
import globalStore from '../../store';
import { DataContext } from '../../player/DataContext';
import customEventHandler from '../../helper/customEvents';
import MathEditor from '../../components/MathEditor';
import { saveState } from '../../helper/stateManagerHelper';
import { registerMultiSelectSaveEntries } from '../../helper/multiSelectSaveCoordinator';
import { selectTool } from '../../helper/helper';
import { announce } from '../../helper/announcer';
import { getImageSrc } from '../../helper/imageBlobCache';
import { t } from 'i18next';
import { useMultiSelectDragSource } from '../../helper/hooks/useMultiSelectDrag';
// Reliable touch-primary detection — works on modern iPads (desktop UA)
var IS_TOUCH_DEVICE = typeof window !== 'undefined' && !!((_a = window.matchMedia) === null || _a === void 0 ? void 0 : _a.call(window, '(pointer: coarse)').matches);
/** Convert raw LaTeX to screen-reader-friendly text */
function latexToReadable(latex) {
    if (!latex)
        return '';
    var work = latex.replace(/^\\\(/, '').replace(/\\\)$/, '').trim();
    work = work.replace(/\\frac\{([^}]*)\}\{([^}]*)\}/g, '$1 over $2');
    work = work.replace(/\\frac(\d)(\d)/g, '$1 over $2');
    work = work.replace(/\^\{([^}]*)\}/g, ' to the power of $1');
    work = work.replace(/\^(\d)/g, ' to the power of $1');
    work = work.replace(/\\times/g, 'times');
    work = work.replace(/\\div/g, 'divided by');
    work = work.replace(/\\cdot/g, 'times');
    work = work.replace(/\\pm/g, 'plus or minus');
    work = work.replace(/\\le(?:q)?/g, 'less than or equal to');
    work = work.replace(/\\ge(?:q)?/g, 'greater than or equal to');
    work = work.replace(/\\lt/g, 'less than');
    work = work.replace(/\\gt/g, 'greater than');
    work = work.replace(/\\ne(?:q)?/g, 'not equal to');
    work = work.replace(/\\%/g, 'percent');
    work = work.replace(/\\\$/g, 'dollars');
    work = work.replace(/\\pi/g, 'pi');
    work = work.replace(/\\sqrt\{([^}]*)\}/g, 'square root of $1');
    work = work.replace(/\\;/g, ' ');
    work = work.replace(/\\,/g, ' ');
    work = work.replace(/\\placeholder\{[^}]*\}/g, '');
    work = work.replace(/[{}]/g, '');
    work = work.replace(/\\/g, '');
    work = work.replace(/\s+/g, ' ').trim();
    return work;
}
/** Extract readable text from a card's text content (string or MathEditor array) */
function extractCardText(text) {
    if (!text)
        return '';
    if (typeof text === 'string')
        return latexToReadable(text);
    if (!Array.isArray(text))
        return '';
    return text
        .map(function (item) {
        return item.type === 'math' ? latexToReadable(item.latex || item.value || '') : item.value || '';
    })
        .join(' ')
        .trim();
}
var Card = function (props) {
    var id = props.id, center = props.center, dimension = props.dimension, _a = props.rotate, rotate = _a === void 0 ? false : _a, cardText = props.cardText, _b = props.flip, flip = _b === void 0 ? false : _b, positionLock = props.positionLock, scale = props.scale, zIndex = props.zIndex, _c = props.type, type = _c === void 0 ? 'text' : _c, backImage = props.backImage, deckSizeOnly = props.deckSizeOnly;
    var Z_BASE = 10;
    var zoomLevel = globalStore.useStoreState(function (store) { return store.playerStore; }).zoomLevel;
    /** ---------------- DATA STORE FROM GAME CONTROLLER ---------------- */
    var _d = useContext(DataContext), setManipulative = _d.setManipulative, manipulative = _d.manipulative, selectedTool = _d.selectedTool, setSelectedTool = _d.setSelectedTool, setContextMenu = _d.setContextMenu, panningEnable = _d.panningEnable;
    var _e = useMultiSelectDragSource(id, selectedTool), startMultiDrag = _e.startMultiDrag, moveMultiDrag = _e.moveMultiDrag, endMultiDrag = _e.endMultiDrag;
    var bottomToolConfig = globalStore.useStoreState(function (store) { return store.jsonDataStore; }).bottomToolConfig;
    // const [isFlipped, setIsFlipped] = useState(flip);
    var _f = useState(false), isFocused = _f[0], setIsFocused = _f[1];
    var _g = useState(false), animating = _g[0], setAnimating = _g[1];
    var _h = useState(null), calculatedFontSize = _h[0], setCalculatedFontSize = _h[1];
    var _j = useState(false), shouldWrap = _j[0], setShouldWrap = _j[1];
    var hasMeasuredRef = useRef(false);
    var measureContainerRef = useRef(null);
    var cardBtnRef = useRef(null);
    var _k = useMemo(function () {
        var decks = manipulative.filter(function (m) { return m.data.type === 'deck'; });
        for (var i = 0; i < decks.length; i++) {
            var cards = decks[i].data.cards;
            if (!(cards === null || cards === void 0 ? void 0 : cards.some(function (c) { return c.id === id; })))
                continue;
            // Sort by the immutable per-card UUID (`id`) so cardNumber stays stable
            // for a card's lifetime — restack/shuffle reorders the cards array,
            // and using the live array index here was making the aria-label of
            // unrelated dealt cards change whenever a sibling was restacked.
            var sortedIds = __spreadArray([], cards, true).sort(function (a, b) {
                return String(a.id).localeCompare(String(b.id));
            });
            var stableIdx = sortedIds.findIndex(function (c) { return c.id === id; });
            return { deckNumber: i + 1, cardNumber: stableIdx + 1 };
        }
        return { deckNumber: 0, cardNumber: 0 };
    }, [manipulative, id]), deckNumber = _k.deckNumber, cardNumber = _k.cardNumber;
    // const toggleFlip = useCallback(() => {
    //   setIsFlipped(prev => !prev);
    // }, []);
    var cardImages = {
        portrait: {
            frontFace: './assets/images/manipulative/cards/popup/cardFrontPortrait.svg',
            backFace: './assets/images/manipulative/cards/popup/cardBackPortrait.svg',
        },
        landscape: {
            frontFace: './assets/images/manipulative/cards/popup/cardFrontLandscape.svg',
            backFace: './assets/images/manipulative/cards/popup/cardBackLandscape.svg',
        },
    };
    var orientation = rotate ? 'landscape' : 'portrait';
    var BASE_CARD_FONT_SIZE = 100;
    var WRAP_THRESHOLD_FONT_SIZE = 22;
    /** Measure text content and calculate font size. Returns true if successful. */
    var measureFontSize = useCallback(function () {
        var container = measureContainerRef.current;
        if (!container) {
            hasMeasuredRef.current = true;
            return true;
        }
        var editor = container.querySelector('.displayCard .editorDiv');
        if (!editor) {
            hasMeasuredRef.current = true;
            return true;
        }
        var marginW = container.clientWidth * 0.1;
        var marginH = container.clientHeight * 0.1;
        var availW = container.clientWidth - marginW * 2;
        var availH = container.clientHeight - marginH * 2;
        if (availW <= 0 || availH <= 0) {
            hasMeasuredRef.current = true;
            return true;
        }
        // Phase 1: measure at BASE size, single-line (no wrap)
        var origFontSize = editor.style.fontSize;
        var origW = editor.style.width;
        var origH = editor.style.height;
        var origO = editor.style.overflow;
        var origWS = editor.style.whiteSpace;
        editor.style.fontSize = "".concat(BASE_CARD_FONT_SIZE, "px");
        editor.style.width = 'max-content';
        editor.style.height = 'max-content';
        editor.style.overflow = 'visible';
        editor.style.whiteSpace = 'nowrap';
        var cW = editor.scrollWidth;
        var cH = editor.scrollHeight;
        // MathEditor content not ready yet — signal caller to retry
        if (cW === 0 || cH === 0) {
            editor.style.fontSize = origFontSize;
            editor.style.width = origW;
            editor.style.height = origH;
            editor.style.overflow = origO;
            editor.style.whiteSpace = origWS;
            return false;
        }
        var scale = Math.min(availW / cW, availH / cH);
        var singleLineSize = Math.floor(BASE_CARD_FONT_SIZE * scale);
        if (singleLineSize >= WRAP_THRESHOLD_FONT_SIZE) {
            // Fits comfortably — use single-line scaled size
            editor.style.fontSize = origFontSize;
            editor.style.width = origW;
            editor.style.height = origH;
            editor.style.overflow = origO;
            editor.style.whiteSpace = origWS;
            hasMeasuredRef.current = true;
            setShouldWrap(false);
            setCalculatedFontSize(Math.min(singleLineSize, BASE_CARD_FONT_SIZE));
        }
        else {
            // Phase 2: text is too long — wrap at threshold size and fit to height
            editor.style.fontSize = "".concat(WRAP_THRESHOLD_FONT_SIZE, "px");
            editor.style.width = "".concat(availW, "px");
            editor.style.height = 'max-content';
            editor.style.overflow = 'visible';
            editor.style.whiteSpace = 'normal';
            var wrappedH = editor.scrollHeight;
            editor.style.fontSize = origFontSize;
            editor.style.width = origW;
            editor.style.height = origH;
            editor.style.overflow = origO;
            editor.style.whiteSpace = origWS;
            var size = wrappedH <= availH
                ? WRAP_THRESHOLD_FONT_SIZE
                : Math.max(1, Math.floor(WRAP_THRESHOLD_FONT_SIZE * (availH / wrappedH)));
            hasMeasuredRef.current = true;
            setShouldWrap(true);
            setCalculatedFontSize(size);
        }
        return true;
    }, []);
    var cardTextKey = JSON.stringify(cardText);
    var filteredText = useMemo(function () {
        return Array.isArray(cardText)
            ? cardText.filter(function (item) { return item.type !== 'text' || item.value !== '\u200B'; })
            : cardText;
    }, 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cardTextKey]);
    var displayData = useMemo(function () { return ({
        type: 'data',
        data: {
            text: filteredText,
            color: '#000000',
            alignment: 'center',
            size: calculatedFontSize !== null && calculatedFontSize !== void 0 ? calculatedFontSize : BASE_CARD_FONT_SIZE,
        },
    }); }, [filteredText, calculatedFontSize]);
    useEffect(function () {
        if (type !== 'text')
            return;
        var cancelled = false;
        var retries = 0;
        var maxRetries = 5;
        var attempt = function () {
            if (cancelled)
                return;
            var success = measureFontSize();
            // MathEditor content not ready — retry
            if (!success && retries < maxRetries) {
                retries++;
                setTimeout(attempt, 100);
            }
        };
        var timer = setTimeout(attempt, 50);
        return function () {
            cancelled = true;
            clearTimeout(timer);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cardTextKey, orientation]);
    var _l = useState(null), dragStart = _l[0], setDragStart = _l[1];
    var _m = useState(center), position = _m[0], setPosition = _m[1];
    var _o = useState(positionLock), lock = _o[0], setLock = _o[1];
    var _p = useState(null), dragStartItem = _p[0], setDragStartItem = _p[1];
    var dragStartRef = useRef(null);
    var hasMoved = useRef(false);
    var dragStated = useRef(false);
    var lastDragPosRef = useRef(null);
    var inputSourceRef = useRef(null);
    var keyMoveStartRef = useRef(null);
    var keyMoveCurPosRef = useRef(null);
    // Multi-select move refs
    var selectedToolRef = useRef(selectedTool);
    useEffect(function () {
        selectedToolRef.current = selectedTool;
    }, [selectedTool]);
    var bringToFront = function (cardId) {
        setManipulative(function (prev) {
            return prev.map(function (item) {
                if (item.data.type !== 'deck')
                    return item;
                var cards = item.data.cards;
                var target = cards.find(function (c) { return c.id === cardId; });
                if (!target)
                    return item;
                // Sort cards by current zIndex
                var sorted = __spreadArray([], cards, true).sort(function (a, b) { var _a, _b; return ((_a = a.zIndex) !== null && _a !== void 0 ? _a : Z_BASE) - ((_b = b.zIndex) !== null && _b !== void 0 ? _b : Z_BASE); });
                // If already on top, do nothing
                if (sorted[sorted.length - 1].id === cardId) {
                    return item;
                }
                // Move target card to top
                var reordered = __spreadArray(__spreadArray([], sorted.filter(function (c) { return c.id !== cardId; }), true), [target], false);
                // Normalize zIndex
                var normalized = reordered.map(function (c, i) { return (__assign(__assign({}, c), { zIndex: Z_BASE + i })); });
                return __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { cards: normalized }) });
            });
        });
    };
    var getPoint = function (e) {
        if ('touches' in e && e.touches.length > 0) {
            return { x: e.touches[0].clientX, y: e.touches[0].clientY };
        }
        if ('changedTouches' in e && e.changedTouches.length > 0) {
            return { x: e.changedTouches[0].clientX, y: e.changedTouches[0].clientY };
        }
        return {
            x: e.clientX,
            y: e.clientY,
        };
    };
    var startDrag = function (e) {
        var _a = getPoint(e), clientX = _a.x, clientY = _a.y;
        dragStartRef.current = { x: clientX, y: clientY };
        hasMoved.current = false;
        setDragStart({
            x: clientX - position.x * zoomLevel,
            y: clientY - position.y * zoomLevel,
        });
        var foundDeck = manipulative.find(function (m) { return m.data.type === 'deck' && 'cards' in m.data && m.data.cards.some(function (c) { return c.id === id; }); });
        var foundItem = foundDeck && 'cards' in foundDeck.data
            ? foundDeck.data.cards.find(function (card) { return card.id === id; })
            : null;
        setDragStartItem(foundItem ? JSON.parse(JSON.stringify(foundItem.position)) : null);
        dragStated.current = true;
        bringToFront(id);
        startMultiDrag();
    };
    var onTouchStart = function (e) {
        // If touch already owns this interaction, ignore
        if (panningEnable)
            return;
        e.preventDefault();
        e.stopPropagation();
        if (inputSourceRef.current === 'touch')
            return;
        inputSourceRef.current = 'touch';
        startDrag(e);
    };
    var onMouseDown = function (e) {
        if (panningEnable)
            return;
        // Ignore right-click — it opens the context menu, not a drag
        if (e.button === 2)
            return;
        e.preventDefault();
        e.stopPropagation();
        // Ignore synthetic mouse events after touch
        if (inputSourceRef.current === 'touch')
            return;
        inputSourceRef.current = 'mouse';
        startDrag(e);
    };
    useEffect(function () {
        var handleMove = function (e) {
            var _a, _b;
            if ('touches' in e) {
                e.preventDefault();
            }
            if (!dragStartRef.current)
                return;
            if (!lock) {
                var _c = getPoint(e), clientX = _c.x, clientY = _c.y;
                // movement threshold
                var dx = clientX - dragStartRef.current.x;
                var dy = clientY - dragStartRef.current.y;
                if (!hasMoved.current && Math.sqrt(dx * dx + dy * dy) > 5) {
                    hasMoved.current = true;
                }
                if (!hasMoved.current)
                    return;
                var newX_1 = (clientX - ((_a = dragStart === null || dragStart === void 0 ? void 0 : dragStart.x) !== null && _a !== void 0 ? _a : 0)) / zoomLevel;
                var newY_1 = (clientY - ((_b = dragStart === null || dragStart === void 0 ? void 0 : dragStart.y) !== null && _b !== void 0 ? _b : 0)) / zoomLevel;
                setManipulative(function (_man) {
                    return _man.map(function (_item) {
                        if (_item.data.type !== 'deck')
                            return _item;
                        return __assign(__assign({}, _item), { data: __assign(__assign({}, _item.data), { cards: _item.data.cards.map(function (_card) {
                                    return _card.id === id
                                        ? __assign(__assign({}, _card), { position: { x: newX_1, y: newY_1 } }) : _card;
                                }) }) });
                    });
                });
                lastDragPosRef.current = { x: newX_1, y: newY_1 };
                setPosition({ x: newX_1, y: newY_1 });
                // Multi-select: broadcast delta so other selected items follow
                if (dragStartItem) {
                    moveMultiDrag(newX_1 - dragStartItem.x, newY_1 - dragStartItem.y);
                }
            }
        };
        var handleEnd = function (e) {
            var _a;
            if (!dragStart)
                return;
            setSelectedTool(selectTool(e, id, selectedTool, false));
            // Multi-select group move: batch saveState, skip snap-to-grid, signal end
            var isMultiSelectSource = hasMoved.current &&
                selectedToolRef.current.length > 1 &&
                selectedToolRef.current.includes(id);
            if (isMultiSelectSource) {
                var finalPos = (_a = lastDragPosRef.current) !== null && _a !== void 0 ? _a : position;
                if (dragStartItem) {
                    var entry = {
                        type: id,
                        id: id,
                        propertyName: 'position',
                        old: JSON.stringify(dragStartItem),
                        new: JSON.stringify(finalPos),
                    };
                    if (!registerMultiSelectSaveEntries(id, [entry])) {
                        saveState(entry);
                    }
                }
                endMultiDrag();
                dragStated.current = false;
                dragStartRef.current = null;
                setTimeout(function () {
                    inputSourceRef.current = null;
                }, 0);
                setDragStart(null);
                setDragStartItem(null);
                return;
            }
            if (!hasMoved.current) {
            }
            if (!lock && hasMoved.current) {
                var getSnappedPosition_1 = function (targetPos, cards, currentId, width, height) {
                    var SNAP_OFFSET = 15;
                    var SNAP_RANGE_X = width * 0.5;
                    var SNAP_RANGE_Y = height * 0.5;
                    var closestCard = null;
                    var minDistance = Infinity;
                    for (var _i = 0, cards_1 = cards; _i < cards_1.length; _i++) {
                        var card = cards_1[_i];
                        if (card.id === currentId)
                            continue;
                        if (!card.position)
                            continue;
                        var dx = targetPos.x - card.position.x;
                        var dy = targetPos.y - card.position.y;
                        // rectangular snap zone
                        if (Math.abs(dx) <= SNAP_RANGE_X && Math.abs(dy) <= SNAP_RANGE_Y) {
                            var distance = Math.hypot(dx, dy); // true distance
                            if (distance < minDistance) {
                                minDistance = distance;
                                closestCard = card;
                            }
                        }
                    }
                    if (closestCard) {
                        return {
                            x: closestCard.position.x + SNAP_OFFSET,
                            y: closestCard.position.y + SNAP_OFFSET,
                        };
                    }
                    return targetPos;
                };
                var _oldData_1;
                var _newData_1;
                var _newState_1 = manipulative.map(function (_item) {
                    var _a;
                    if (_item.data.type !== 'deck')
                        return _item;
                    // mouseup
                    var finalPos = (_a = lastDragPosRef.current) !== null && _a !== void 0 ? _a : position;
                    var snappedPosition = getSnappedPosition_1(finalPos, _item.data.cards, id, dimension.width, dimension.height);
                    _oldData_1 = dragStartItem !== null ? dragStartItem : undefined;
                    var _newDeck = __assign(__assign({}, _item), { data: __assign(__assign({}, _item.data), { cards: _item.data.cards.map(function (_card) {
                                if (_card.id === id) {
                                    _newData_1 = snappedPosition;
                                    var _newItem = __assign(__assign({}, _card), { position: _newData_1 });
                                    return _newItem;
                                }
                                else {
                                    return _card;
                                }
                            }) }) });
                    // _newData =
                    return _newDeck;
                });
                if (_oldData_1 && _newData_1) {
                    saveState({
                        type: id,
                        id: id.split('_')[1],
                        propertyName: 'position',
                        old: JSON.stringify(_oldData_1),
                        new: JSON.stringify(_newData_1),
                    });
                }
                setManipulative(function () { return _newState_1; });
            }
            dragStated.current = false;
            dragStartRef.current = null;
            setTimeout(function () {
                inputSourceRef.current = null;
            }, 0);
            setDragStart(null);
            setDragStartItem(null);
        };
        if (dragStart !== null && !panningEnable) {
            window.addEventListener('mousemove', handleMove);
            window.addEventListener('mouseup', handleEnd);
            window.addEventListener('touchmove', handleMove, { passive: false });
            window.addEventListener('touchend', handleEnd);
            window.addEventListener('touchcancel', handleEnd);
        }
        return function () {
            window.removeEventListener('mousemove', handleMove);
            window.removeEventListener('mouseup', handleEnd);
            window.removeEventListener('touchmove', handleMove);
            window.removeEventListener('touchend', handleEnd);
            window.removeEventListener('touchcancel', handleEnd);
        };
    }, [
        dragStart,
        lock,
        panningEnable,
        zoomLevel,
        JSON.stringify(manipulative),
        JSON.stringify(dragStartItem),
    ]);
    useEffect(function () {
        if (center)
            setPosition(center);
    }, [center]);
    useEffect(function () {
        setLock(positionLock);
    }, [positionLock]);
    var prevSelectedToolRef = useRef([]);
    useEffect(function () {
        var wasFocused = prevSelectedToolRef.current.includes(id);
        var nowFocused = selectedTool.includes(id);
        prevSelectedToolRef.current = selectedTool;
        setIsFocused(nowFocused);
        if (nowFocused && !wasFocused && cardBtnRef.current) {
            requestAnimationFrame(function () {
                var _a, _b;
                // Don't steal focus from the context menu
                if ((_a = document.activeElement) === null || _a === void 0 ? void 0 : _a.closest('.shape-context-menu'))
                    return;
                (_b = cardBtnRef.current) === null || _b === void 0 ? void 0 : _b.focus();
            });
        }
    }, [selectedTool]);
    /** ---------------- BOTTOM PANEL EVENT ---------------- */
    useEffect(function () {
        var onToolClick = function (data) {
            switch (data.id) {
                case 'flip': {
                    var selected = selectedToolRef.current.filter(function (tid) { return tid.startsWith('card_'); });
                    if (selected[0] === id) {
                        var decks_1 = manipulative.filter(function (m) { return m.data.type === 'deck'; });
                        // Group by deck + new side
                        var groups_1 = {};
                        selected.forEach(function (cardId) {
                            for (var i = 0; i < decks_1.length; i++) {
                                var cards = decks_1[i].data.cards;
                                var card = cards === null || cards === void 0 ? void 0 : cards.find(function (c) { return c.id === cardId; });
                                if (card) {
                                    var newSide = card.showImage === 'back' ? 'front' : 'back';
                                    var key = "".concat(i + 1, ":").concat(newSide);
                                    groups_1[key] = (groups_1[key] || 0) + 1;
                                    break;
                                }
                            }
                        });
                        var details = Object.entries(groups_1)
                            .map(function (_a) {
                            var key = _a[0], count = _a[1];
                            var _b = key.split(':'), deck = _b[0], side = _b[1];
                            var sideText = side === 'front' ? t('text.frontSidesVisible') : t('text.backSidesVisible');
                            return t('text.flipDetail', { count: count, deck: deck, side: sideText });
                        })
                            .join('. ');
                        announce(details);
                    }
                    break;
                }
                case 'lock':
                    // console.log('lock in card &&&');
                    // setManipulative(_man =>
                    //   _man.map(_item => {
                    //     if (_item.data.type !== 'deck') return _item;
                    //     return {
                    //       ..._item,
                    //       data: {
                    //         ..._item.data,
                    //         cards: _item.data.cards.map(_card =>
                    //           _card.id === id
                    //             ? {
                    //                 ..._card,
                    //                 lock: {
                    //                   positioning: !_card.lock.positioning,
                    //                   editing: _card.lock.editing ?? false,
                    //                 },
                    //               }
                    //             : _card
                    //         ),
                    //       },
                    //     };
                    //   })
                    // );
                    break;
            }
        };
        if (isFocused) {
            customEventHandler.addEventListener('bottomPanel', onToolClick);
        }
        return function () {
            customEventHandler.removeEventListener('bottomPanel', onToolClick);
        };
    }, [isFocused, JSON.stringify(manipulative)]);
    return (_jsx(_Fragment, { children: _jsxs("div", { className: "cardWrapperPerspective", style: {
                width: dimension === null || dimension === void 0 ? void 0 : dimension.width,
                height: dimension === null || dimension === void 0 ? void 0 : dimension.height,
                position: 'absolute',
                top: (position === null || position === void 0 ? void 0 : position.y) - (dimension === null || dimension === void 0 ? void 0 : dimension.height) / 2,
                left: (position === null || position === void 0 ? void 0 : position.x) - (dimension === null || dimension === void 0 ? void 0 : dimension.width) / 2,
                transform: "scale(".concat(scale, ")"),
                transformOrigin: 'center center',
                cursor: lock || panningEnable ? 'inherit' : 'pointer',
                zIndex: zIndex,
                boxShadow: isFocused && !animating ? "0px 0px 20px #5063AE" : undefined,
                borderRadius: "10px",
            }, onMouseDown: onMouseDown, onTouchStart: onTouchStart, children: [_jsx("div", { style: { position: 'absolute', inset: 0, pointerEvents: 'none' }, inert: true, children: _jsxs("div", { className: "cardWrapper ".concat(flip ? 'isFlipped' : ''), onTransitionStart: function () { return setAnimating(true); }, onTransitionEnd: function () { return setAnimating(false); }, "aria-hidden": "true", inert: true, children: [_jsxs("div", { className: "cardFace frontface", "aria-hidden": flip, inert: true, style: deckSizeOnly ? { border: '2px solid rgb(40, 50, 115)', borderRadius: '10px' } : undefined, children: [_jsx("img", { className: "frontCardImg", src: cardImages[orientation].frontFace, alt: "", "aria-hidden": "true", inert: true, draggable: false, style: {
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover',
                                            outline: isFocused ? "1px solid rgb(40, 50, 115)" : "1px solid transparent",
                                            borderRadius: "10px",
                                        } }), _jsxs("div", { ref: measureContainerRef, className: "cardText ".concat(orientation).concat(shouldWrap ? ' wrap-text' : ''), style: type === 'text' && !hasMeasuredRef.current ? { visibility: 'hidden' } : undefined, "aria-hidden": "true", inert: true, children: [type === 'text' && cardText && (_jsx(MathEditor, { data: displayData, color: "#000000", className: "displayCard", isTextUpdate: false, idPreffix: "cardPopupImageWhiteBoard_".concat(id), index: 0, keypadButtons: bottomToolConfig.mathKeypad, bottomBtns: bottomToolConfig.mathKeypadBottom, disableTransform: true, 
                                                // isManipulative
                                                viewonly: true }, "cardMathEditor-".concat(id))), type === 'image' && typeof cardText === 'string' && (_jsx("img", { src: getImageSrc(cardText), className: "cardImage", draggable: false, alt: t('text.cardImage'), onLoad: function (e) {
                                                    var img = e.currentTarget;
                                                    img.classList.remove('cardImage--svg');
                                                    if (img.naturalHeight / img.naturalWidth < 1.2) {
                                                        img.classList.add('cardImage--svg');
                                                    }
                                                }, "aria-hidden": "true", inert: true }))] })] }), _jsx("div", { className: "cardFace backface", "aria-hidden": !flip, style: deckSizeOnly ? { border: '2px solid rgb(40, 50, 115)', borderRadius: '10px' } : undefined, children: _jsx("img", { className: "backCardImg", src: backImage !== null && backImage !== void 0 ? backImage : cardImages[orientation].backFace, alt: "", "aria-hidden": "true", draggable: false, style: {
                                        outline: isFocused ? "1px solid rgb(40, 50, 115)" : "1px solid transparent",
                                        borderRadius: "10px",
                                        background: backImage ? 'transparent' : "rgb(40, 50, 115)",
                                    } }) })] }) }), _jsx("button", { ref: cardBtnRef, id: id, "data-manipulative-id": id, className: "cardFocusBtn", tabIndex: 0, "aria-label": t('text.playingCardLabel', {
                        deckNumber: deckNumber,
                        cardNumber: cardNumber,
                        content: type === 'text'
                            ? " ".concat(extractCardText(cardText) || t('text.blank'))
                            : " ".concat(t('text.imageCard')),
                        side: flip ? t('text.backSideVisible') : t('text.frontSideVisible'),
                    }), "aria-roledescription": t('text.playingCard'), onClick: function (e) {
                        var _a;
                        if (panningEnable)
                            return;
                        if (inputSourceRef.current === 'touch' || e.detail === 0) {
                            if (!selectedTool.includes(id))
                                setSelectedTool([id]);
                            var rect = (_a = cardBtnRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
                            if (rect) {
                                setContextMenu({ x: rect.right, y: rect.top, manipulativeId: id });
                            }
                        }
                    }, onKeyDown: function (e) {
                        var _a, _b;
                        if (panningEnable)
                            return;
                        if (e.key === 'Enter' ||
                            e.key === ' ' ||
                            e.key === 'ContextMenu' ||
                            (e.shiftKey && e.key === 'F10')) {
                            e.preventDefault();
                            e.stopPropagation();
                            if (!selectedTool.includes(id)) {
                                setSelectedTool([id]);
                            }
                            var rect = (_a = cardBtnRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
                            if (rect) {
                                setContextMenu({ x: rect.right, y: rect.top, manipulativeId: id });
                            }
                            return;
                        }
                        if (!lock && ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                            e.preventDefault();
                            var curPos = (_b = keyMoveCurPosRef.current) !== null && _b !== void 0 ? _b : position;
                            if (!keyMoveStartRef.current) {
                                keyMoveStartRef.current = __assign({}, curPos);
                            }
                            var step = e.shiftKey ? 50 : 10;
                            var delta = {
                                ArrowUp: { x: 0, y: -step },
                                ArrowDown: { x: 0, y: step },
                                ArrowLeft: { x: -step, y: 0 },
                                ArrowRight: { x: step, y: 0 },
                            }[e.key];
                            var newCenter_1 = { x: curPos.x + delta.x, y: curPos.y + delta.y };
                            keyMoveCurPosRef.current = newCenter_1;
                            setPosition(newCenter_1);
                            setManipulative(function (prev) {
                                return prev.map(function (item) {
                                    if (item.data.type !== 'deck')
                                        return item;
                                    return __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { cards: item.data.cards.map(function (c) {
                                                return c.id === id ? __assign(__assign({}, c), { position: newCenter_1 }) : c;
                                            }) }) });
                                });
                            });
                        }
                    }, onKeyUp: function (e) {
                        if (keyMoveStartRef.current &&
                            keyMoveCurPosRef.current &&
                            ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                            var newPos = keyMoveCurPosRef.current;
                            saveState({
                                type: id,
                                id: id.split('_')[1],
                                propertyName: 'position',
                                old: JSON.stringify(keyMoveStartRef.current),
                                new: JSON.stringify(newPos),
                            });
                            var dirs = [];
                            if (newPos.y < keyMoveStartRef.current.y)
                                dirs.push(t('text.up'));
                            if (newPos.y > keyMoveStartRef.current.y)
                                dirs.push(t('text.down'));
                            if (newPos.x < keyMoveStartRef.current.x)
                                dirs.push(t('text.left'));
                            if (newPos.x > keyMoveStartRef.current.x)
                                dirs.push(t('text.right'));
                            announce(t('text.cardMovedInDeck', {
                                number: cardNumber,
                                deck: deckNumber,
                                direction: dirs.join(', '),
                                x: Math.round(newPos.x),
                                y: Math.round(newPos.y),
                            }));
                            keyMoveStartRef.current = null;
                            keyMoveCurPosRef.current = null;
                        }
                    }, style: {
                        position: 'absolute',
                        inset: 0,
                        background: 'transparent',
                        border: 'none',
                        padding: 0,
                        margin: 0,
                        cursor: 'inherit',
                    } })] }, id) }));
};
export default Card;
